import React from 'react';

import getDataServiceUrl from '@amzn/awsboost-ui-common/lib/bpdsWebAppConfig/getDataServiceUrl';
import { SpaceBetween, Link, Icon } from '@amzn/awsui-components-react-v3';

import {
  getEnvironment,
  Environment,
  alphaBoostPortalEndpoint,
  betaBoostPortalEndpoint,
  prodBoostPortalEndpoint,
  prodChinaBoostPortalEndpoint,
  prodDCABoostPortalEndpoint,
  prodLCKBoostPortalEndpoint,
} from '@amzn/awsboost-ui-common/lib';

import { httpFetch, BOOST_EXECUTION_URLS } from '../hooks/useHttp';
import {ReceiveExecutionType, ReceiveType} from '../store/types';

export interface BoostMetaDataUnifiedReceiveRequest {
  referenceId: string;
  referenceType?: ReceiveType;
  receivingSite: string;
  carrier?: string;
  trackingNumber?: string;
  vendor?: string;
  receiveType?: ReceiveExecutionType;
}
type BoostMetaDataUnifiedReceiveParams = Omit<BoostMetaDataUnifiedReceiveRequest, "receiveType">;

export interface BoostMetaDataInventoryAdjustmentRequest {
  receiveType?: ReceiveExecutionType;
  receivingSite: string;
  inventoryAdjustmentType?: string;
}
type BoostMetaDataInventoryAdjustmentParams = Omit<BoostMetaDataInventoryAdjustmentRequest, "receiveType">;

export interface BoostMetaDataRequest {
  receiveType?: ReceiveExecutionType;
  shipmentDetails?: BoostMetaDataUnifiedReceiveParams,
  inventoryAdjustmentDetails?: BoostMetaDataInventoryAdjustmentParams,
  clientName: string,
  // client work request id for the boost workflow
  boostWorkRequestId: string,
}

// InitApplication Request to get the DCA Boost worker details
const INIT_APPLICATION_REQUEST = {
  operationName: 'InitApplication',
  variables: {
    namespace: 'global',
    withSkills: true,
  },
  query:
    'query InitApplication($namespace: String, $withSkills: Boolean!) {\n  workerV2 {\n    worker {\n      id\n      login\n      firstName\n      lastName\n      email\n      photo {\n        standard\n        hiRes\n        __typename\n      }\n      manager {\n        id\n        login\n        firstName\n        lastName\n        __typename\n      }\n      managerData {\n        worker {\n          id\n          login\n          firstName\n          lastName\n          __typename\n        }\n        __typename\n      }\n      skillAwards @include(if: $withSkills) {\n        skillArn\n        awardStatus\n        skillDefinition {\n          name\n          __typename\n        }\n        __typename\n      }\n      directReports {\n        firstName\n        lastName\n        login\n        id\n        email\n        jobTitle\n        location\n        teams {\n          teamId\n          workerId\n          teamAttributes {\n            teamName\n            __typename\n          }\n          typeId\n          __typename\n        }\n        __typename\n      }\n      teams {\n        teamId\n        workerId\n        teamAttributes {\n          teamName\n          __typename\n        }\n        typeId\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n  preferences(namespace: $namespace) {\n    version\n    data\n    __typename\n  }\n  authorization {\n    permissionsV2 {\n      name\n      teams\n      __typename\n    }\n    teamsV2\n    __typename\n  }\n  activeFlagsStatic {\n    activeFlags\n    __typename\n  }\n}\n',
};

export const boostConfig = () => {
  const environment = getEnvironment(window.location.hostname);
  const CHINA_ENVIRONENTS = [Environment.GAMMA_CHINA, Environment.PROD_CHINA];
  // zhy environments needs to be configured
  return {
    frameworkStage: environment,
    region: CHINA_ENVIRONENTS.includes(environment) ? 'zhy' : 'not-zhy', // this needs a further discussion
  };
};

export const getInitApplicationData = () => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Usewfmv2', 'true');

  // TBD - configure for prod and zhy environments
  return fetch(getDataServiceUrl(window, 'platform'), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(INIT_APPLICATION_REQUEST),
  })
    .then((res) => res.json())
    .then((res) => normalizeWorkerData(res.data?.workerV2?.worker));
};

export const normalizeWorkerData = (data) => {
  return {
    user: {
      ...data,
      userFullName: `${data.firstName} ${data.lastName}`,
      username: data?.login,
    },
  };
};

// reference from Boost Metrics Package - https://code.amazon.com/packages/AWSBoostUI/blobs/f3459f75ae2954ddec990bf4e225fa8309c36d14/--/packages/metrics/src/StandaloneUIMetricsPublisher/index.ts#L47
export const getPortalRequestUrl = () => {
  const environment = getEnvironment(window.location.hostname);

  switch (environment) {
    case Environment.ALPHA:
      return alphaBoostPortalEndpoint;
    case Environment.BETA:
      return betaBoostPortalEndpoint;
    case Environment.PROD:
      return prodBoostPortalEndpoint;
    case Environment.PROD_CHINA:
      return prodChinaBoostPortalEndpoint;
    case Environment.PROD_DCA:
      return prodDCABoostPortalEndpoint;
    case Environment.PROD_LCK:
      return prodLCKBoostPortalEndpoint;
    default:
      return `app.${environment}.boost.aws.a2z.com`;
  }
};

export const BOOST_CLIENT_NAME = 'BOOST_WORKFLOW';

export const BOOST_EXCEPTION_CLIENT_SAME = 'BOOST_INGESTION_WORKFLOW';

export const BoostExceptionWorkRequestUrl = ({ workRequestId, title }: { workRequestId: string; title: string }) => {
  const url = `https://${getPortalRequestUrl()}/platform/work-requests/${workRequestId}`;
  return (
    <Link href={url} target="_blank">
      <SpaceBetween size="xs" direction="horizontal">
        <Icon name="external" />
        <span>{title} </span>
      </SpaceBetween>
    </Link>
  );
};

//  function to publish boost meta data to boost catalog service.
// TBD - enable batching to reduce the number of requests
export const updateBoostMetaDataUnifiedReceive =
    async (workRequestId: string, request: BoostMetaDataUnifiedReceiveRequest) => {
  const unifiedReceiveRequest: BoostMetaDataRequest = {
    shipmentDetails: {
      ...request,
      receiveType: undefined
    },
    clientName:'BOOST_WORKFLOW',
    boostWorkRequestId: workRequestId,
    receiveType: request?.receiveType,
  };
  await updateBoostMetaData(unifiedReceiveRequest);
};

//  function to publish boost meta data to boost catalog service.
// TBD - enable batching to reduce the number of requests
export const updateBoostMetaDataInventoryAdjustment =
    async (workRequestId: string, request: BoostMetaDataInventoryAdjustmentRequest) => {

  const unifiedReceiveRequest: BoostMetaDataRequest = {
    inventoryAdjustmentDetails: {
      ...request,
      receiveType: undefined
    },
    clientName:'BOOST_WORKFLOW',
    boostWorkRequestId: workRequestId,
    receiveType: request?.receiveType,
  };
  await updateBoostMetaData(unifiedReceiveRequest);
};

//  function to publish boost meta data to boost catalog service.
// TBD - enable batching to reduce the number of requests
const updateBoostMetaData = async (request: BoostMetaDataRequest) => {
      try {
        await httpFetch(BOOST_EXECUTION_URLS.metadata, {
          headers: {
            'content-type': 'application/json; charset=utf-8',
          },
          body: {
            ...request
          },
          method: 'POST',
        });
      } catch (e) {
        console.error('updateBoostMetaData: error', e);
      }
    };
